import type { Ref } from 'react'
import { Alert, Descriptions, Tag } from 'antd'
import { formatShanghaiDateTime } from '@/const/date.ts'
import { AttachmentUpload } from '@/features/files/index.ts'
import { formatAmount } from '@/utils/money.ts'
import type { FinanceAccount, GroupDetailData } from './finance.types.ts'
import { GroupBillsTable } from './group-bills-table.tsx'
import { snapshotObject } from './utils/finance-snapshot.ts'

function groupStatusColor(status: string) {
  if (status === 'issued') {
    return 'success'
  }
  if (status === 'draft') {
    return 'gold'
  }
  return 'default'
}

function groupStatusLabel(status: string) {
  if (status === 'issued') {
    return '已出单'
  }
  if (status === 'draft') {
    return '草稿'
  }
  return '已作废'
}

export function GroupDetailContent({
  group: g,
  displayAccount,
  printRef,
  onBill,
  historical = false
}: {
  group: GroupDetailData
  displayAccount: FinanceAccount | Record<string, unknown> | undefined
  printRef: Ref<HTMLDivElement>
  onBill: (id: number) => void
  historical?: boolean
}) {
  const account = snapshotObject(g.accountSnapshot)
  const customer = snapshotObject(g.customerSnapshot)
  return (
    <div className="space-y-4 px-6 py-4">
      {g.status === 'voided' && (
        <Alert showIcon type="warning" title="已作废 · 仅供历史核对" description={g.voidReason} />
      )}
      <div ref={printRef} className="space-y-4 print:bg-white print:text-black">
        <section>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            <h2 className="text-lg font-semibold">{String(customer.name ?? g.customerName)}</h2>
            <Tag color={groupStatusColor(g.status)}>{groupStatusLabel(g.status)}</Tag>
            {!historical && (
              <Tag color={g.collectionHeldAt ? 'warning' : 'default'}>
                {g.collectionHeldAt ? '暂缓催收' : '正常催收'}
              </Tag>
            )}
            <span className="text-content-soft text-xs">归属月份：{g.billingMonth ?? '未指定'}</span>
          </div>
          <p className="hidden text-sm tabular-nums print:block">{g.groupNo}</p>
          {!historical && g.collectionHoldReason && (
            <p className="text-content-muted mt-2 text-sm">处理原因：{g.collectionHoldReason}</p>
          )}
          <div className="bg-surface-muted mt-3 grid grid-cols-1 gap-3 rounded-lg px-4 py-3 sm:grid-cols-2">
            <div>
              <p className="text-content-muted mb-1 text-xs">{historical ? '该版本保存时待收金额' : '当前待收金额'}</p>
              <p className="text-2xl font-semibold tracking-tight tabular-nums">
                <span className="mr-1 text-lg font-normal">¥</span>
                {g.status === 'voided' ? '—' : formatAmount(g.outstandingAmount)}
              </p>
            </div>
            <div className="border-border border-t pt-3 sm:border-t-0 sm:border-l sm:pt-0 sm:pl-4">
              <p className="text-content-muted mb-1 text-xs">{g.status === 'draft' ? '预计出单金额' : '出单金额'}</p>
              <p className="text-xl font-medium tabular-nums">
                ¥{formatAmount(g.status === 'draft' ? g.outstandingAmount : g.totalAmountSnapshot)}
              </p>
              <p className="text-content-soft mt-1 text-xs">
                {g.issuedAt ? formatShanghaiDateTime(g.issuedAt) : '确认出单后生成收款单'}
              </p>
            </div>
          </div>
        </section>
        <section>
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-sm font-semibold">账单明细</h3>
            <span className="text-content-soft text-xs">共 {g.items.length} 笔</span>
          </div>
          <GroupBillsTable items={g.items} onBill={onBill} />
        </section>
        <section className="border-border border-t pt-5">
          <h3 className="mb-4 text-sm font-semibold">收款账户</h3>
          <Descriptions
            size="small"
            colon={false}
            column={1}
            items={[
              {
                key: 'holder',
                label: '收款单位',
                children: String(displayAccount?.accountHolder ?? '未选择收款账户')
              },
              { key: 'bank', label: '开户银行', children: String(displayAccount?.bankName ?? '—') },
              {
                key: 'number',
                label: '收款账号',
                children: <span className="font-mono break-all">{String(displayAccount?.accountNo ?? '—')}</span>
              }
            ]}
          />
          {typeof account.qrFileId === 'number' && (
            <AttachmentUpload purpose="moneyAttachment" value={[account.qrFileId]} readOnly />
          )}
        </section>
        {g.note && (
          <section className="border-border border-t pt-5">
            <h3 className="mb-2 text-sm font-semibold">备注</h3>
            <p className="text-content-muted text-sm leading-6 break-words whitespace-pre-wrap">{g.note}</p>
          </section>
        )}
      </div>
      <p className="text-content-soft text-xs leading-5">
        {historical
          ? '正在查看保存的历史快照。实际付款与当前余额请返回当前版本查看。'
          : '出单金额保留出单时的快照，当前待收随收款更新。可通过修订增删本次收款项目，旧版本继续保留。'}
      </p>
    </div>
  )
}
